// ThreeScene.tsx
// Three.js 3D sphere visualization with sphere rotation controls

import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

interface ThreeSceneProps {
  onPointMove: (position: THREE.Vector3) => void;
  isInitialized: boolean;
}

export const ThreeScene: React.FC<ThreeSceneProps> = ({ onPointMove, isInitialized }) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const pointRef = useRef<THREE.Mesh | null>(null);
  const sphereRef = useRef<THREE.Mesh | null>(null);
  const markerPointsRef = useRef<THREE.Group | null>(null);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!mountRef.current || !isInitialized) return;

    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1a1a2e);
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      45,
      mountRef.current.clientWidth / mountRef.current.clientHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 4);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Sphere
    const sphereGeometry = new THREE.SphereGeometry(1, 64, 64);
    const sphereMaterial = new THREE.MeshPhongMaterial({
      color: 0x2563eb,
      wireframe: true,
      opacity: 0.2,
      transparent: true
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    scene.add(sphere);
    sphereRef.current = sphere;

    // Add visual markers to show rotation - create a group that rotates with sphere
    const markersGroup = new THREE.Group();
    
    // Add colored dots at cardinal points to show rotation
    const markerGeometry = new THREE.SphereGeometry(0.04, 16, 16);
    
    // Equator markers (red)
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const marker = new THREE.Mesh(
        markerGeometry,
        new THREE.MeshPhongMaterial({ color: 0xff4444 })
      );
      marker.position.set(Math.cos(angle) * 1.02, 0, Math.sin(angle) * 1.02);
      markersGroup.add(marker);
    }
    
    // Meridian markers (green)
    for (let i = 1; i < 8; i++) {
      const angle = (i / 8) * Math.PI;
      const marker = new THREE.Mesh(
        markerGeometry,
        new THREE.MeshPhongMaterial({ color: 0x44ff44 })
      );
      marker.position.set(0, Math.cos(angle) * 1.02, Math.sin(angle) * 1.02);
      markersGroup.add(marker);
    }
    
    sphere.add(markersGroup);
    markerPointsRef.current = markersGroup;

    // Main point (pink - fixed in world space, not rotating with sphere)
    // Position at front facing camera (0, 0, 1) instead of top (0, 1, 0)
    const pointGeometry = new THREE.SphereGeometry(0.04, 32, 32);
    const pointMaterial = new THREE.MeshPhongMaterial({ color: 0xec4899 });
    const point = new THREE.Mesh(pointGeometry, pointMaterial);
    point.position.set(0, 0, 1);
    pointRef.current = point;
    scene.add(point);

    // Lighting
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);
    scene.add(new THREE.AmbientLight(0x404040));

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Calculate and notify of point position relative to sphere
    const notifyPointPosition = () => {
      if (!sphereRef.current || !pointRef.current) return;
      
      // Force matrix update
      sphereRef.current.updateMatrixWorld(true);
      
      // Get point position in world space
      const pointWorldPos = new THREE.Vector3();
      pointRef.current.getWorldPosition(pointWorldPos);
      
      // Convert to sphere's local space
      const sphereWorldToLocal = new THREE.Matrix4();
      sphereWorldToLocal.copy(sphereRef.current.matrixWorld).invert();
      
      const pointLocal = pointWorldPos.clone().applyMatrix4(sphereWorldToLocal);
      pointLocal.normalize();
      
      console.log('Point position in sphere space:', pointLocal);
      onPointMove(pointLocal);
    };

    // Mouse controls for sphere rotation
    const handleMouseDown = (event: MouseEvent) => {
      isDraggingRef.current = true;
      previousMouseRef.current = { x: event.clientX, y: event.clientY };
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (!isDraggingRef.current || !sphereRef.current) return;

      const deltaX = event.clientX - previousMouseRef.current.x;
      const deltaY = event.clientY - previousMouseRef.current.y;
      
      previousMouseRef.current = { x: event.clientX, y: event.clientY };

      // Rotate sphere incrementally
      sphereRef.current.rotation.y += deltaX * 0.01;
      sphereRef.current.rotation.x += deltaY * 0.01;

      // Update color palette
      notifyPointPosition();
    };

    const handleResize = () => {
      if (!mountRef.current) return;
      camera.aspect = mountRef.current.clientWidth / mountRef.current.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mountRef.current.clientWidth, mountRef.current.clientHeight);
    };

    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    // Set initial position
    notifyPointPosition();

    return () => {
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [onPointMove, isInitialized]);

  return <div ref={mountRef} className="w-full h-full" />;
};